/**
 * ScopeChips — one-tap-removable summary of what the next message is scoped to:
 * incident workspace, selected files, layers (with datetime window / drawn
 * area), and any other active data sources. Derived purely from
 * `realtimeSelections`, so it never disagrees with what is sent.
 */
import React from 'react';
import { XIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

import { type ScopeChip } from './chatHelpers';

export {
  buildScopeChips,
  type BuildScopeChipsInput,
  type ScopeChip,
} from './chatHelpers';

export interface ScopeChipsProps {
  chips: ScopeChip[];
  disabled?: boolean;
}

const ScopeChips: React.FC<ScopeChipsProps> = ({ chips, disabled }) => {
  if (chips.length === 0) return null;
  return (
    <ul
      aria-label="Message scope"
      data-testid="ai-scope-chips"
      className="tw:m-0 tw:flex tw:list-none tw:flex-wrap tw:gap-1 tw:p-0"
    >
      {chips.map((chip) => (
        <li key={chip.key}>
          <Badge
            variant={chip.onRemove ? 'secondary' : 'outline'}
            className="tw:max-w-[16rem] tw:gap-0.5 tw:pr-0.5"
          >
            <span className="tw:truncate">{chip.label}</span>
            {chip.onRemove && (
              <Button
                variant="ghost"
                size="icon-xs"
                className="tw:size-4 tw:rounded-full"
                aria-label={`Remove ${chip.label}`}
                disabled={disabled}
                onClick={chip.onRemove}
              >
                <XIcon className="tw:size-3" />
              </Button>
            )}
          </Badge>
        </li>
      ))}
    </ul>
  );
};

export default ScopeChips;
